import { getApiBaseUrl } from '../config/runtime';
import { authStorage } from './authStorage';

const API_BASE_URL = getApiBaseUrl();

class UploadService {
  /**
   * Get auth headers with Bearer token
   * @returns {object} - Headers object with Authorization if token exists
   */
  async getAuthHeaders() {
    const token = await authStorage.getToken();
    const headers = {};
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Detect if running on mobile device
   * @returns {boolean}
   */
  isMobileDevice() {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Detect if running on iOS
   * @returns {boolean}
   */
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Compress image for mobile upload
   * @param {File} file - Original image file
   * @param {number} maxWidth - Maximum width (default: 1920)
   * @param {number} maxHeight - Maximum height (default: 1080) 
   * @param {number} quality - JPEG quality 0-1 (default: 0.8)
   * @returns {Promise<File>} - Compressed image file
   */
  async compressImage(file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            console.log('📱 Image compressed:', {
              original: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
              compressed: `${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
              reduction: `${(((file.size - compressedFile.size) / file.size) * 100).toFixed(1)}%`
            });
            
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Upload an image file to the server with mobile optimization
   * @param {File} file - The image file to upload
   * @param {function} onProgress - Optional progress callback (percent: number) => void
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async uploadImage(file, onProgress = null) {
    const isMobile = this.isMobileDevice();
    const isIOS = this.isIOS();
    
    try {
      // Validate file before upload
      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error
        };
      }

      console.log('📤 Starting image upload:', {
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        device: isMobile ? (isIOS ? 'iOS' : 'Android') : 'Desktop'
      });

      let uploadFile = file;

      // Compress image for mobile devices to prevent upload failures
      if (isMobile && file.size > 3 * 1024 * 1024) { // 3MB threshold
        console.log('📱 Compressing image for mobile upload...');
        try {
          uploadFile = await this.compressImage(
            file, 
            isIOS ? 1280 : 1920, // iOS gets more aggressive compression
            isIOS ? 720 : 1080,
            isIOS ? 0.7 : 0.8
          );
        } catch (compressionError) {
          console.warn('⚠️ Image compression failed, using original:', compressionError);
          uploadFile = file;
        }
      }

      const formData = new FormData();
      formData.append('image', uploadFile);

      // Mobile-optimized fetch options
      const fetchOptions = {
        method: 'POST',
        body: formData,
        headers: {
          ...(await this.getAuthHeaders()),
          // Don't set Content-Type header - let browser set it with boundary for FormData
        },
        credentials: 'include',
      };

      // Add mobile-specific timeout settings
      if (isMobile) {
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for mobile
        
        fetchOptions.signal = controller.signal;

        try {
          const response = await this._uploadWithRetry(fetchOptions, 2); // 2 retries for mobile
          clearTimeout(timeoutId);
          
          const result = await response.json();

          if (!response.ok) {
            console.error('❌ Upload failed:', result);
            return {
              success: false,
              error: result.detail || result.message || 'Upload failed'
            };
          }

          console.log('✅ Mobile upload successful:', result);
          return {
            success: true,
            data: result.data
          };
          
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      } else {
        // Desktop upload (original logic)
        const response = await fetch(`${API_BASE_URL}/upload/image`, fetchOptions);
        const result = await response.json();

        if (!response.ok) {
          console.error('❌ Upload failed:', result);
          return {
            success: false,
            error: result.detail || result.message || 'Upload failed'
          };
        }

        console.log('✅ Upload successful:', result);
        return {
          success: true,
          data: result.data
        };
      }

    } catch (error) {
      console.error('❌ Upload error:', error);
      
      // Better error messages for mobile
      let errorMessage = error.message || 'Network error during upload';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Upload timed out. Please check your connection and try again.';
      } else if (error.message.includes('fetch')) {
        errorMessage = isMobile 
          ? 'Network error. Please check your mobile connection and try again.'
          : 'Network error during upload';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Upload an audio file to the server
   * @param {File|Blob} file - The audio file/blob to upload
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async uploadAudio(file) {
    try {
      // Basic validation
      if (!file) return { success: false, error: 'No audio file provided' };
      
      // Check size (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        return { success: false, error: 'Audio file too large (max 20MB)' };
      }

      const formData = new FormData();
      // Ensure filename has extension if it's a blob
      let filename = file.name || 'recording.webm';
      if (!filename.includes('.')) filename += '.webm'; // Default to webm for browser recording
      
      formData.append('audio', file, filename);

      const fetchOptions = {
        method: 'POST',
        body: formData,
        headers: {
          ...(await this.getAuthHeaders()),
        },
        credentials: 'include',
      };

      const response = await fetch(`${API_BASE_URL}/upload/audio`, fetchOptions);
      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Audio upload failed:', result);
        return {
          success: false,
          error: result.detail || result.message || 'Audio upload failed'
        };
      }

      return {
        success: true,
        data: result.data
      };

    } catch (error) {
      console.error('❌ Audio upload error:', error);
      return {
        success: false,
        error: error.message || 'Network error during audio upload'
      };
    }
  }

  /**
   * Upload with retry logic for mobile
   * @private
   */
  async _uploadWithRetry(fetchOptions, maxRetries = 2) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📱 Upload attempt ${attempt + 1}/${maxRetries + 1}`);
        const response = await fetch(`${API_BASE_URL}/upload/image`, fetchOptions);
        
        if (response.ok || response.status < 500) {
          // Success or client error (don't retry client errors)
          return response;
        }
        
        throw new Error(`Server error: ${response.status}`);
        
      } catch (error) {
        lastError = error;
        console.warn(`📱 Upload attempt ${attempt + 1} failed:`, error.message);
        
        if (attempt < maxRetries && error.name !== 'AbortError') {
          // Wait before retry (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          console.log(`📱 Retrying upload in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Validate an image file before upload
   * @param {File} file - The file to validate
   * @returns {{valid: boolean, error?: string}}
   */
  validateImageFile(file) {
    // Check if file exists
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    // Check for GIF files and provide helpful message
    if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) {
      return {
        valid: false,
        error: 'GIF images aren\'t supported for AI analysis yet. Please try uploading a JPG, PNG, or WebP image instead.'
      };
    }

    // Check file type (excluding GIF)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Invalid file type. Please select a JPEG, PNG, or WebP image.' 
      };
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(2);
      return { 
        valid: false, 
        error: `File too large (${sizeMB}MB). Maximum size is 10MB.` 
      };
    }

    return { valid: true };
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted size string
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get supported file extensions for file input
   * @returns {string} - Comma-separated list of extensions
   */
  getSupportedExtensions() {
    return '.jpg,.jpeg,.png,.webp';
  }

  /**
   * Get supported MIME types for file input
   * @returns {string} - Comma-separated list of MIME types
   */
  getSupportedMimeTypes() {
    return 'image/jpeg,image/jpg,image/png,image/webp';
  }
}

const uploadService = new UploadService();
export default uploadService; 